import { OpenInNewOutlined, PlayArrowOutlined } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { MouseEvent, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { myFolder } from '../../redux/consts';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { Prompt } from '../../Types/prompt';
import { openSafeExternalLink } from '../../util/url';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';

interface PromptRunButtonProps {
  isAIAppStore: boolean;
  prompt: Prompt;
  size?: number;
}

const PromptRunButton = ({ isAIAppStore, prompt, size = 24 }: PromptRunButtonProps) => {
  const analytics = useAnalytics();
  const navigate = useClassicNavigate();

  const { currentFolder } = useSelector(selectFolders);

  const executePrompt = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      const isExternalPrompt = Boolean(prompt.externalLink);
      analytics.event(
        AnalyticsEvent.WM_NIGEL_PROMPT_CLICK,
        {
          id: prompt.id,
        },
        {
          promptId: prompt.id,
          isExternal: isExternalPrompt,
        }
      );

      if (isExternalPrompt) {
        const linkOpened = openSafeExternalLink(prompt.externalLink);
        if (!linkOpened) {
          enqueueSnackbar(`Unable to open the external link for ${prompt.title}`, {
            variant: 'error',
          });
        }
        return;
      }

      const query = new URLSearchParams({ prompt: prompt.id });

      navigate(`/folder/${currentFolder?.id ?? myFolder.id}/new-chat?${query.toString()}`);
    },
    [prompt.id, prompt.title, prompt.externalLink, currentFolder, analytics, navigate]
  );

  return (
    <Tooltip
      title={`${isAIAppStore ? 'Open App' : prompt?.externalLink ? 'Open Prompt' : 'Run Prompt'}`}
    >
      <IconButton
        aria-label="run"
        onClick={executePrompt}
        sx={{
          backgroundColor: '#0047FF',
          '&:hover': { backgroundColor: '#0037cc' },
          width: size,
          height: size,
          borderRadius: '50%',
        }}
      >
        {prompt?.externalLink ? (
          <OpenInNewOutlined htmlColor="white" sx={{ fontSize: `${size - 8}px` }} />
        ) : (
          <PlayArrowOutlined htmlColor="white" sx={{ fontSize: `${size - 4}px` }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default PromptRunButton;
